// /multimodal/agent/src/engines/prompt-engineering-tool-call-engine.ts
import { EventEmitter } from 'events';
import { v4 as uuidv4 } from 'uuid';
import type { AgentStreamingEvent, ToolCall } from '../types';

type ParsingState = 'normal' | 'in_tool_call' | 'collecting_tool_name' | 'collecting_tool_call';

interface StreamingToolCall {
  id: string;
  name: string;
  arguments: string;
  argumentsStarted: boolean;
  nameComplete: boolean;
}

export class PromptEngineeringToolCallEngine extends EventEmitter {
  private state: ParsingState = 'normal';
  private buffer = '';
  private currentToolCall: StreamingToolCall | null = null;
  private streamingToolCallUpdates: Map<string, StreamingToolCall> = new Map();
  private messageId: string;
  private toolCallIdCounter = 0;

  constructor(messageId: string) {
    super();
    this.messageId = messageId;
  }

  processChunk(chunk: string): void {
    this.buffer += chunk;

    while (this.buffer.length > 0) {
      const processed = this.processBuffer();
      if (!processed) break;
    }
  }

  private processBuffer(): boolean {
    switch (this.state) {
      case 'normal':
        return this.handleNormalState();
      case 'in_tool_call':
        return this.handleInToolCallState();
      case 'collecting_tool_name':
        return this.handleCollectingToolNameState();
      case 'collecting_tool_call':
        return this.handleCollectingToolCallState();
      default:
        return false;
    }
  }

  private handleNormalState(): boolean {
    const toolCallStart = this.buffer.indexOf('<tool_call>');

    if (toolCallStart === -1) {
      // Emit all remaining content as normal content
      if (this.buffer.length > 0) {
        this.emitContentChunk(this.buffer);
        this.buffer = '';
      }
      return false;
    }

    // Emit content before tool_call as normal content
    if (toolCallStart > 0) {
      this.emitContentChunk(this.buffer.slice(0, toolCallStart));
    }

    // Remove processed content and transition to tool_call state
    this.buffer = this.buffer.slice(toolCallStart + '<tool_call>'.length);
    this.state = 'in_tool_call';
    this.initializeNewToolCall();

    return true;
  }

  private handleInToolCallState(): boolean {
    // Look for JSON start
    const jsonStartMatch = this.buffer.match(/^\s*{/);
    if (jsonStartMatch) {
      const matchLength = jsonStartMatch[0].length;
      this.buffer = this.buffer.slice(matchLength);

      // Start collecting tool call content
      this.state = 'collecting_tool_call';
      this.currentToolCall!.argumentsStarted = true;

      // Emit the opening brace as first argument chunk
      this.emitToolCallChunk('{');

      return true;
    }

    // If we have more than reasonable whitespace, something is wrong
    if (this.buffer.length > 10) {
      this.handleError('Invalid tool call format');
      return false;
    }

    return false;
  }

  private handleCollectingToolNameState(): boolean {
    // This state is not used in current flow, but kept for completeness
    return false;
  }

  private handleCollectingToolCallState(): boolean {
    const toolCallEnd = this.buffer.indexOf('</tool_call>');

    if (toolCallEnd === -1) {
      // No end tag found, emit all current buffer content as tool call arguments
      if (this.buffer.length > 0 && this.currentToolCall?.nameComplete) {
        this.emitToolCallChunk(this.buffer);
        this.buffer = '';
      }
      return false;
    }

    // Found end tag, process content before it
    const toolCallContent = this.buffer.slice(0, toolCallEnd);

    if (toolCallContent.length > 0) {
      this.parseAndEmitToolCallContent(toolCallContent);
    }

    // Complete the tool call
    this.completeCurrentToolCall();

    // Remove processed content and return to normal state
    this.buffer = this.buffer.slice(toolCallEnd + '</tool_call>'.length);
    this.state = 'normal';

    return true;
  }

  private parseAndEmitToolCallContent(content: string): void {
    if (!this.currentToolCall) return;

    try {
      // Try to parse the JSON to extract tool name if not already extracted
      if (!this.currentToolCall.nameComplete) {
        const nameMatch = content.match(/"name"\s*:\s*"([^"]+)"/);
        if (nameMatch) {
          this.currentToolCall.name = nameMatch[1];
          this.currentToolCall.nameComplete = true;
        }
      }

      // If tool name is complete, emit the content as arguments
      if (this.currentToolCall.nameComplete) {
        this.emitToolCallChunk(content);
      }
    } catch (error) {
      // If parsing fails, still emit the content if tool name is known
      if (this.currentToolCall.nameComplete) {
        this.emitToolCallChunk(content);
      }
    }
  }

  private initializeNewToolCall(): void {
    this.toolCallIdCounter++;
    this.currentToolCall = {
      id: `call_${Date.now()}_${this.generateRandomId()}`,
      name: '',
      arguments: '',
      argumentsStarted: false,
      nameComplete: false,
    };
  }

  private emitToolCallChunk(argumentChunk: string): void {
    if (!this.currentToolCall || !this.currentToolCall.nameComplete) return;

    this.currentToolCall.arguments += argumentChunk;

    // Update streaming tool call
    this.streamingToolCallUpdates.set(this.currentToolCall.id, { ...this.currentToolCall });

    const event: AgentStreamingEvent = {
      id: uuidv4(),
      type: 'assistant_streaming_tool_call',
      timestamp: Date.now(),
      toolCallId: this.currentToolCall.id,
      toolName: this.currentToolCall.name,
      arguments: argumentChunk, // Emit original chunk, not cumulative
      isComplete: false,
      messageId: this.messageId,
    };

    this.emit('streaming_event', event);
  }

  private emitContentChunk(content: string): void {
    const event: AgentStreamingEvent = {
      id: uuidv4(),
      type: 'assistant_streaming_content',
      timestamp: Date.now(),
      content,
      messageId: this.messageId,
    };

    this.emit('streaming_event', event);
  }

  private completeCurrentToolCall(): void {
    if (!this.currentToolCall || !this.currentToolCall.nameComplete) return;

    // Emit final tool call completion event
    const event: AgentStreamingEvent = {
      id: uuidv4(),
      type: 'assistant_streaming_tool_call',
      timestamp: Date.now(),
      toolCallId: this.currentToolCall.id,
      toolName: this.currentToolCall.name,
      arguments: '', // Empty for completion event
      isComplete: true,
      messageId: this.messageId,
    };

    this.emit('streaming_event', event);
    this.currentToolCall = null;
  }

  private handleError(message: string): void {
    this.state = 'normal';
    this.currentToolCall = null;
    this.buffer = '';
    console.error(`PromptEngineeringToolCallEngine error: ${message}`);
  }

  private generateRandomId(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  // Get final parsed tool calls
  getToolCalls(): ToolCall[] {
    const toolCalls: ToolCall[] = [];

    for (const streamingCall of this.streamingToolCallUpdates.values()) {
      if (streamingCall.nameComplete && streamingCall.arguments) {
        try {
          const parsedArguments = JSON.parse(streamingCall.arguments);
          toolCalls.push({
            id: streamingCall.id,
            name: streamingCall.name,
            arguments: parsedArguments,
          });
        } catch (error) {
          console.error(`Failed to parse tool call arguments: ${error}`);
        }
      }
    }

    return toolCalls;
  }

  // Finalize processing
  finalize(): void {
    // Process any remaining buffer content
    if (this.buffer.length > 0) {
      if (this.state === 'normal') {
        this.emitContentChunk(this.buffer);
      } else if (this.state === 'collecting_tool_call' && this.currentToolCall?.nameComplete) {
        this.parseAndEmitToolCallContent(this.buffer);
        this.completeCurrentToolCall();
      }
    }

    this.buffer = '';
    this.state = 'normal';
    this.currentToolCall = null;
  }
}
